import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpService } from 'src/app/services/http/http-service.service';
import { TimeTable } from 'src/app/types/time-table/time-table.type';
import { Id, Ids } from 'src/app/models/database/types/id.model';
import { Observable } from 'rxjs';
import { Employee } from 'src/app/models/database/employee';
import { Insurance } from 'src/app/models/database/insurance.model';
import { Patients } from 'src/app/models/database/patient';
import { AddSchedule } from 'src/app/types/schedule/add-schedule.type';
import { Schedules, Schedule, ScheduleFilter } from 'src/app/types/schedule';
import { ProceduresGroupFilter, ProceduresGroups } from 'src/app/types/procedures-group';
import { Procedures } from 'src/app/types/procedure';

@Injectable({
  providedIn: 'root'
})
export class ScheduleService extends HttpService {

  constructor(private http: HttpClient) {
    super('schedule')
  }

  async registerTimeTable(timeTable: TimeTable, companyId: number): Promise<number> {
    return this.http.post<number>(this.url + `/timetable/company/${companyId}`, { timeTable }).toPromise()
  }

  add(schedule: AddSchedule, proceduresGroupsIds: number[]): Observable<number> {
    return this.http.post<number>(this.url, { schedule, proceduresGroupsIds })
  }

  addInsurances(scheduleId: Id, insurancesIds: Ids): Observable<void> {
    return this.http.post<void>(`${this.url}/${scheduleId}/insurance`, { insurancesIds })
  }

  addDoctors(scheduleId: Id, employeesIds: Ids): Observable<void> {
    return this.http.post<void>(`${this.url}/${scheduleId}/doctors`, { employeesIds })
  }

  addProceduresGroups(scheduleId: number, proceduresGroupsIds: number[]): Observable<number> {
    return this.http.post<number>(`${this.url}/${scheduleId}/procedures-groups`, { proceduresGroupsIds })
  }

  get(scheduleId: number | Id): Observable<Schedule> {
    return this.http.get<Schedule>(`${this.url}/${scheduleId}`)
  }

  getAll(filter?: ScheduleFilter): Observable<Schedules> {
    let url: string = `${this.url}`
    if (filter) {
      url += this.implementQueries(filter)
    }
    return this.http.get<Schedules>(url)
  }

  getInsurances(scheduleId: Id, filter?: Insurance): Observable<any> {
    let url: string = `${this.url}/${scheduleId}/insurances`
    if (filter) {
      url += this.implementQueries(filter)
    }
    return this.http.get(url)
  }

  getDoctors(scheduleId: Id, filter?: Employee): Observable<{ employee: Employee, clinicEmployeeId: Id }[]> {
    let url: string = `${this.url}/${scheduleId}/doctors`
    if (filter) {
      url += this.implementQueries(filter)
    }
    return this.http.get<{ employee: Employee, clinicEmployeeId: Id }[]>(url)
  }

  getPatients(scheduleId: Id, patientName?: string): Observable<Patients> {
    let url: string = `${this.url}/${scheduleId}/patients`
    if (patientName) {
      url = `${url}/?name=${patientName}`
    }
    return this.http.get<Patients>(url)
  }

  getProcedures(scheduleId: Id): Observable<Procedures> {
    return this.http.get<Procedures>(`${this.url}/${scheduleId}/procedures`)
  }

  getProceduresGroups(scheduleId: Id, filter?: ProceduresGroupFilter): Observable<ProceduresGroups> {
    let url: string = `${this.url}/${scheduleId}/procedures-groups`
    if (filter) {
      url += this.implementQueries(filter)
    }
    return this.http.get<ProceduresGroups>(url)
  }

  edit(schedule: Schedule): Observable<number> {
    return this.http.put<number>(`${this.url}/${schedule.id}`, { schedule })
  }

  delete(scheduleId: Id): Observable<void> {
    return this.http.delete<void>(`${this.url}/${scheduleId}`)
  }

  deleteDoctor(scheduleId: Id, doctorId: Id): Observable<void> {
    return this.http.delete<void>(`${this.url}/${scheduleId}/doctor/${doctorId}`)
  }

  deleteProceduresGroup(scheduleId: number, proceduresGroupId: number): Observable<number> {
    return this.http.delete<number>(`${this.url}/${scheduleId}/procedures-group/${proceduresGroupId}`)
  }

  exists(name: string): Observable<void> {
    return this.http.head<void>(`${this.url}/validate-name?name=${name}`)
  }
}